import { useState, useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import toast from "react-hot-toast";
import { Loader2, Pencil, Upload } from "lucide-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { useAuth } from "@/contexts/AuthContext";
import { PageHeader } from "@/components/shared/PageHeader";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PasswordInput } from "@/components/ui/password-input";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { PasswordStrengthIndicator } from "@/components/shared/PasswordStrengthIndicator";
import {
  getMyAccountantProfile,
  saveMyAccountantProfile,
  setPassword,
  uploadAccountantStudioLogo,
} from "@/services/authService";

const MIN_PASSWORD_LENGTH = 8;

function getDisplayName(
  firstName: string | null,
  lastName: string | null,
  displayName: string | null,
  email: string,
): string {
  const parts = [firstName, lastName].filter(Boolean);
  if (parts.length > 0) return parts.join(" ");
  if (displayName?.trim()) return displayName;
  return email;
}

function getInitials(
  email: string,
  firstName?: string | null,
  lastName?: string | null,
): string {
  if (firstName && lastName)
    return `${firstName[0]}${lastName[0]}`.toUpperCase();
  if (firstName) return firstName.slice(0, 2).toUpperCase();
  return email.slice(0, 2).toUpperCase();
}

export function AccountantProfilePage(): JSX.Element {
  const { t } = useTranslation();
  const { user, refreshUser } = useAuth();
  const queryClient = useQueryClient();

  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordSaving, setPasswordSaving] = useState(false);

  const profileQueryKey = ["accountant", user?.id ?? null, "profile"] as const;
  const { data: accountantProfile, isLoading: loadingProfile } = useQuery({
    queryKey: profileQueryKey,
    queryFn: getMyAccountantProfile,
    enabled: Boolean(user?.id),
  });

  const [kind, setKind] = useState<"solo" | "studio">("solo");
  const [studioName, setStudioName] = useState("");
  const [studioAddress, setStudioAddress] = useState("");
  const [studioPhone, setStudioPhone] = useState("");
  const [studioLogoUrl, setStudioLogoUrl] = useState<string | null>(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);

  // Hydrate the studio form once the profile fetch settles so users can edit
  // their existing values rather than re-typing from scratch.
  useEffect(() => {
    const p = accountantProfile?.profile;
    if (!p) return;
    setKind(p.kind);
    setStudioName(p.studioName ?? "");
    setStudioAddress(p.studioAddress ?? "");
    setStudioPhone(p.studioPhone ?? "");
    setStudioLogoUrl(p.studioLogoUrl ?? null);
  }, [accountantProfile]);

  const displayName = getDisplayName(
    user?.firstName ?? null,
    user?.lastName ?? null,
    user?.displayName ?? null,
    user?.email ?? "",
  );

  const saveStudio = useMutation({
    mutationFn: saveMyAccountantProfile,
    onSuccess: async () => {
      toast.success(
        t("accountantProfile.studio.saved", "Estudio actualizado."),
      );
      await queryClient.invalidateQueries({ queryKey: profileQueryKey });
    },
    onError: () => {
      toast.error(
        t("accountantProfile.studio.saveError", "No pudimos guardar."),
      );
    },
  });

  const handleSaveStudio = (e: React.FormEvent<HTMLFormElement>): void => {
    e.preventDefault();
    if (kind === "studio" && studioName.trim().length === 0) {
      toast.error(
        t(
          "accountantProfile.studio.nameRequired",
          "Ingresá el nombre del estudio.",
        ),
      );
      return;
    }
    saveStudio.mutate({
      kind,
      ...(kind === "studio"
        ? {
            studioName: studioName.trim(),
            studioAddress: studioAddress.trim() || undefined,
            studioPhone: studioPhone.trim() || undefined,
            ...(studioLogoUrl ? { studioLogoUrl } : {}),
          }
        : {}),
    });
  };

  const handleLogoUpload = async (
    e: React.ChangeEvent<HTMLInputElement>,
  ): Promise<void> => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingLogo(true);
    try {
      const { studioLogoUrl: url } = await uploadAccountantStudioLogo(file);
      setStudioLogoUrl(url);
      // Persist immediately so a half-finished form change doesn't drop the
      // logo. Server-side it overrides only the logo field, leaving the
      // rest untouched.
      await saveMyAccountantProfile({
        kind,
        ...(kind === "studio"
          ? {
              studioName: studioName.trim() || studioName,
              studioLogoUrl: url,
              ...(studioAddress.trim()
                ? { studioAddress: studioAddress.trim() }
                : {}),
              ...(studioPhone.trim()
                ? { studioPhone: studioPhone.trim() }
                : {}),
            }
          : {}),
      });
      await queryClient.invalidateQueries({ queryKey: profileQueryKey });
      toast.success(
        t("accountantProfile.studio.logoSaved", "Logo actualizado."),
      );
    } catch {
      toast.error(
        t(
          "accountantProfile.studio.logoError",
          "No pudimos subir el logo.",
        ),
      );
    } finally {
      setUploadingLogo(false);
      e.target.value = "";
    }
  };

  const handleSubmitPassword = useCallback(
    async (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      if (newPassword.length < MIN_PASSWORD_LENGTH) {
        toast.error(
          t("settings.password.error") +
            " " +
            t("settings.password.minLength"),
        );
        return;
      }
      if (newPassword !== confirmPassword) {
        toast.error(t("settings.password.error"));
        return;
      }
      setPasswordSaving(true);
      try {
        await setPassword({
          currentPassword: currentPassword || undefined,
          newPassword,
        });
        await refreshUser();
        setCurrentPassword("");
        setNewPassword("");
        setConfirmPassword("");
        toast.success(t("settings.password.success"));
      } catch {
        toast.error(t("settings.password.error"));
      } finally {
        setPasswordSaving(false);
      }
    },
    [newPassword, confirmPassword, currentPassword, refreshUser, t],
  );

  if (!user) {
    return (
      <div className="fx-content">
        <PageHeader title={t("profile.title", "Perfil")} />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-2xl space-y-4 fx-content">
      <PageHeader
        title={displayName}
        description={t("profile.description", "Datos de tu cuenta")}
        actions={
          <Button variant="outline" asChild>
            <Link
              to="/profile/edit"
              className="inline-flex items-center"
            >
              <Pencil className="mr-2 h-4 w-4" />
              {t("profile.edit", "Editar")}
            </Link>
          </Button>
        }
      />

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">
            {t("profile.personalInfo", "Datos personales")}
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-0">
          <div className="flex flex-col gap-6 sm:flex-row sm:items-start">
            <div className="flex-shrink-0">
              <Avatar className="h-20 w-20 sm:h-24 sm:w-24">
                {user.profileImage ? (
                  <AvatarImage
                    src={user.profileImage}
                    alt={displayName}
                  />
                ) : null}
                <AvatarFallback className="text-lg">
                  {getInitials(
                    user.email,
                    user.firstName,
                    user.lastName,
                  )}
                </AvatarFallback>
              </Avatar>
            </div>
            <div className="min-w-0 flex-1 space-y-3">
              <div>
                <p className="text-xs font-medium text-muted-foreground">
                  {t("profile.firstName", "Nombre")}
                </p>
                <p className="text-sm text-foreground">
                  {user.firstName?.trim() || "—"}
                </p>
              </div>
              <div>
                <p className="text-xs font-medium text-muted-foreground">
                  {t("profile.lastName", "Apellido")}
                </p>
                <p className="text-sm text-foreground">
                  {user.lastName?.trim() || "—"}
                </p>
              </div>
              <div>
                <p className="text-xs font-medium text-muted-foreground">
                  {t("profile.email", "Correo electrónico")}
                </p>
                <p className="text-sm text-foreground">{user.email}</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">
            {t("accountantProfile.studio.title", "Estudio")}
          </CardTitle>
          <CardDescription>
            {t(
              "accountantProfile.studio.description",
              "Cómo se identifica tu práctica para los clientes y en tu portal.",
            )}
          </CardDescription>
        </CardHeader>
        <CardContent className="pt-0">
          {loadingProfile ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              {t("accountantProfile.studio.loading", "Cargando…")}
            </div>
          ) : (
            <form
              onSubmit={handleSaveStudio}
              className="flex w-full flex-col gap-3"
            >
              <div className="w-full space-y-1.5">
                <Label
                  htmlFor="accountant-kind"
                  className="text-xs font-medium text-muted-foreground"
                >
                  {t("accountantProfile.studio.kind", "Modalidad")}
                </Label>
                <Select
                  value={kind}
                  onValueChange={(v) => setKind(v as "solo" | "studio")}
                >
                  <SelectTrigger
                    id="accountant-kind"
                    className="w-full sm:max-w-xs"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="solo">
                      {t(
                        "accountantProfile.studio.kindSolo",
                        "Contador independiente",
                      )}
                    </SelectItem>
                    <SelectItem value="studio">
                      {t(
                        "accountantProfile.studio.kindStudio",
                        "Estudio contable",
                      )}
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {kind === "studio" && (
                <>
                  <div className="w-full space-y-1.5">
                    <Label
                      htmlFor="accountant-studio-name"
                      className="text-xs font-medium text-muted-foreground"
                    >
                      {t(
                        "accountantProfile.studio.name",
                        "Nombre del estudio",
                      )}
                    </Label>
                    <Input
                      id="accountant-studio-name"
                      value={studioName}
                      onChange={(e) => setStudioName(e.target.value)}
                      placeholder={t(
                        "accountantProfile.studio.namePlaceholder",
                        "Ej: Estudio Pérez & Asociados",
                      )}
                    />
                  </div>

                  <div className="w-full space-y-1.5">
                    <Label
                      htmlFor="accountant-studio-address"
                      className="text-xs font-medium text-muted-foreground"
                    >
                      {t(
                        "accountantProfile.studio.address",
                        "Dirección",
                      )}
                    </Label>
                    <Input
                      id="accountant-studio-address"
                      value={studioAddress}
                      onChange={(e) => setStudioAddress(e.target.value)}
                    />
                  </div>

                  <div className="w-full space-y-1.5">
                    <Label
                      htmlFor="accountant-studio-phone"
                      className="text-xs font-medium text-muted-foreground"
                    >
                      {t("accountantProfile.studio.phone", "Teléfono")}
                    </Label>
                    <Input
                      id="accountant-studio-phone"
                      type="tel"
                      value={studioPhone}
                      onChange={(e) => setStudioPhone(e.target.value)}
                      placeholder="099 000 000"
                    />
                  </div>

                  <div className="w-full space-y-1.5">
                    <Label className="text-xs font-medium text-muted-foreground">
                      {t("accountantProfile.studio.logo", "Logo")}
                    </Label>
                    <div className="flex items-center gap-3">
                      <Avatar className="h-12 w-12 rounded-md">
                        {studioLogoUrl ? (
                          <AvatarImage
                            src={studioLogoUrl}
                            alt={
                              studioName ||
                              t(
                                "accountantProfile.studio.logoAlt",
                                "Logo del estudio",
                              )
                            }
                            className="object-contain object-center"
                          />
                        ) : null}
                        <AvatarFallback className="rounded-md text-sm">
                          {(studioName || "ES").slice(0, 2).toUpperCase()}
                        </AvatarFallback>
                      </Avatar>
                      <label
                        className="inline-flex cursor-pointer items-center gap-2 rounded-md border border-input bg-background px-3 py-1.5 text-sm hover:bg-accent"
                        aria-disabled={uploadingLogo}
                      >
                        {uploadingLogo ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Upload className="h-4 w-4" />
                        )}
                        {uploadingLogo
                          ? t(
                              "accountantProfile.studio.logoUploading",
                              "Subiendo…",
                            )
                          : t(
                              "accountantProfile.studio.logoUpload",
                              "Subir logo",
                            )}
                        <input
                          type="file"
                          accept="image/*"
                          className="sr-only"
                          disabled={uploadingLogo}
                          onChange={(e) => {
                            void handleLogoUpload(e);
                          }}
                        />
                      </label>
                    </div>
                  </div>
                </>
              )}

              <div className="flex w-full justify-end">
                <Button type="submit" disabled={saveStudio.isPending}>
                  {saveStudio.isPending ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      {t(
                        "accountantProfile.studio.saving",
                        "Guardando…",
                      )}
                    </>
                  ) : (
                    t("accountantProfile.studio.save", "Guardar estudio")
                  )}
                </Button>
              </div>
            </form>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">
            {t("settings.password.title")}
          </CardTitle>
          <CardDescription>
            {t("settings.password.description")}
          </CardDescription>
        </CardHeader>
        <CardContent className="pt-0">
          <form
            onSubmit={handleSubmitPassword}
            className="flex w-full flex-col gap-3"
          >
            <div className="w-full space-y-1.5">
              <Label
                htmlFor="profile-current-password"
                className="text-xs font-medium text-muted-foreground"
              >
                {t("settings.password.currentPassword")}
              </Label>
              <p className="text-xs text-muted-foreground">
                {t("settings.password.currentPasswordHint")}
              </p>
              <PasswordInput
                id="profile-current-password"
                autoComplete="current-password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                className="w-full transition-colors duration-150"
              />
            </div>
            <div className="w-full space-y-1.5">
              <Label
                htmlFor="profile-new-password"
                className="text-xs font-medium text-muted-foreground"
              >
                {t("settings.password.newPassword")}
              </Label>
              <PasswordInput
                id="profile-new-password"
                autoComplete="new-password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                minLength={MIN_PASSWORD_LENGTH}
                className="w-full transition-colors duration-150"
              />
              <PasswordStrengthIndicator
                password={newPassword}
                showRules
                rulesLabels={{
                  minLength: t(
                    "auth.register.passwordStrength.minLength",
                  ),
                  uppercase: t(
                    "auth.register.passwordStrength.uppercase",
                  ),
                  number: t("auth.register.passwordStrength.number"),
                  special: t("auth.register.passwordStrength.special"),
                }}
                strengthLabels={{
                  weak: t("auth.register.passwordStrength.weak"),
                  fair: t("auth.register.passwordStrength.fair"),
                  good: t("auth.register.passwordStrength.good"),
                  strong: t("auth.register.passwordStrength.strong"),
                }}
              />
            </div>
            <div className="w-full space-y-1.5">
              <Label
                htmlFor="profile-confirm-password"
                className="text-xs font-medium text-muted-foreground"
              >
                {t("settings.password.confirmPassword")}
              </Label>
              <PasswordInput
                id="profile-confirm-password"
                autoComplete="new-password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="w-full transition-colors duration-150"
              />
            </div>
            <div className="flex w-full justify-end">
              <Button type="submit" disabled={passwordSaving}>
                {passwordSaving ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {t("settings.password.saving")}
                  </>
                ) : (
                  t("settings.password.save")
                )}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
